import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent } from 'rxjs';
import { MatNativeDateModule, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { ClickOutsideDirective } from '../../../helper/directives/click-outside.directive';
import { AppPreferencesService } from '../../../services/app-preferences';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import {
  formatMasterDataMonthLabel,
  formatMasterDataDateLabel,
  getCurrentMonthValue,
  getDefaultDailyFromDateValue,
  getMonthEndDateValue,
  getMonthStartDateValue,
  getTodayDateValue,
  MASTER_DATA_FREQUENCIES,
  MASTER_DATA_EXCHANGES,
  MASTER_DATA_PRODUCTS,
} from './market-data-report.data';
import {
  MasterDataExchangeCode,
  MasterDataExchangeOption,
  MasterDataGridDateMap,
  MasterDataGridPeriodMap,
  MasterDataHeaderGroup,
  MasterDataLeafColumn,
  MasterDataProductCode,
  MasterDataProductOption,
  MasterDataFrequencyCode,
  MasterDataRowLabel,
  MasterDataViewMode,
  MasterDataReportResponse,
  MasterDataSummary,
} from './market-data-report.interfaces';

const MASTER_DATA_MAX_RANGE_DAYS = 31;
const MASTER_DATA_MAX_RANGE_MONTHS = 12;
const MASTER_DATA_DEFAULT_BLOCKS = Array.from({ length: 96 }, (_, index) => index + 1);
const MASTER_DATA_METRICS = ['MCP', 'MCV'] as const;
const MASTER_DATA_DEFAULT_FREQUENCY: MasterDataFrequencyCode = '15_MIN';
function formatBsDate(value: string): string {
  const parts = value.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
}

function parseBsDate(value: string): string {
  const parts = value.split("/");
  return parts.length === 3 ? parts[2] + "-" + parts[1].padStart(2, "0") + "-" + parts[0].padStart(2, "0") : value;
}

const MASTER_DATA_MONTH_YEAR_FORMATS = {
  parse: {
    dateInput: null,
  },
  display: {
    dateInput: { month: 'short', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
} as const;

type MasterDataMetricCode = (typeof MASTER_DATA_METRICS)[number];

interface MarketDataBsCalendarCell {
  bs_date: string;
  bs_date_label: string;
  ad_date: string;
  in_current_month: boolean;
  is_today: boolean;
  is_selected: boolean;
}

interface MarketDataBsCalendar {
  bs_year: number;
  bs_month: number;
  bs_month_name: string;
  cells: MarketDataBsCalendarCell[];
}

@Component({
  selector: 'app-market-data-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatNativeDateModule, ClickOutsideDirective],
  templateUrl: './market-data-report.html',
  styleUrl: './market-data-report.scss',
  providers: [{ provide: MAT_DATE_FORMATS, useValue: MASTER_DATA_MONTH_YEAR_FORMATS }],
})
export class MarketDataReportPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;
  protected readonly exchangeOptions: MasterDataExchangeOption[] = MASTER_DATA_EXCHANGES.map((value) => ({ value, label: value }));
  protected readonly productOptions: MasterDataProductOption[] = MASTER_DATA_PRODUCTS.map((value) => ({ value, label: value }));
  protected readonly frequencyOptions = MASTER_DATA_FREQUENCIES;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected errorMessage = '';

  protected selectedExchanges: MasterDataExchangeCode[] = [...MASTER_DATA_EXCHANGES];
  protected selectedProducts: MasterDataProductCode[] = [...MASTER_DATA_PRODUCTS];
  protected selectedFrequency: MasterDataFrequencyCode = MASTER_DATA_DEFAULT_FREQUENCY;
  protected fromMonthPickerDate: Date | null = null;
  protected toMonthPickerDate: Date | null = null;
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: MarketDataBsCalendar | null = null;
  protected activeBsDateControl: 'from_date' | 'to_date' = 'from_date';
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  protected viewMode: MasterDataViewMode = 'BLOCK';
  protected rowAxisTitle = 'Time';
  protected rowLabels: MasterDataRowLabel[] = [];
  protected dateGroups: MasterDataHeaderGroup[] = [];
  private dateLabels: Record<string, string> = {};
  protected exchangeGroups: MasterDataHeaderGroup[] = [];
  protected productGroups: MasterDataHeaderGroup[] = [];
  protected leafColumns: MasterDataLeafColumn[] = [];
  protected periodExchangeGroups: MasterDataHeaderGroup[] = [];
  protected periodProductGroups: MasterDataHeaderGroup[] = [];
  protected periodLeafColumns: MasterDataLeafColumn[] = [];
  protected blockRows: number[] = [...MASTER_DATA_DEFAULT_BLOCKS];
  protected blockTimes: string[] = [];
  protected blockCellMap: MasterDataGridDateMap = {};
  protected periodCellMap: MasterDataGridPeriodMap = {};
  protected metricUnits = {
    MCP: '₹/MWh',
    MCV: 'MW',
  };
  protected summary: MasterDataSummary = {
    date_count: 0,
    row_count: 0,
    exchange_count: 0,
    product_count: 0,
    block_count: 0,
    cell_count: 0,
    frequency: MASTER_DATA_DEFAULT_FREQUENCY,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
  ) {
    this.filtersForm = this.fb.group({
      from_date: [getTodayDateValue(), Validators.required],
      to_date: [getTodayDateValue(), Validators.required],
      data_frequency: [MASTER_DATA_DEFAULT_FREQUENCY, Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    this.bindFilterListeners();
    this.listenToDatePreferenceChanges();
    if (this.isBsDateMode) {
      await this.convertDateControls('BS');
    }
    this.loadReport();
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected async clearFilters(): Promise<void> {
    const fromDateValue = this.getDefaultFromDateForFrequency(this.selectedFrequency);
    const toDateValue = this.getDefaultToDateForFrequency(this.selectedFrequency);
    this.applyFrequencyDefaults(this.selectedFrequency, false);
    this.filtersForm.reset(
      {
        from_date: fromDateValue,
        to_date: toDateValue,
        data_frequency: this.selectedFrequency,
      },
      { emitEvent: false },
    );
    this.syncMonthPickerState();
    this.selectedExchanges = [...MASTER_DATA_EXCHANGES];
    this.selectedProducts = [...MASTER_DATA_PRODUCTS];
    this.errorMessage = '';
    if (this.isBsDateMode) {
      await this.convertDateControls('BS');
    }
    this.loadReport();
  }

  protected handleExportToExcel(): void {
    if (this.isLoading || this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_MASTER_DATA_REPORT, ApiMethod.POST, this.buildReportPayload())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildExportFileName()),
      });
  }

  protected getDateRangeLabel(): string {
    if (this.selectedFrequency === 'MONTHLY') {
      return `${formatMasterDataMonthLabel(this.normalizeMonthValue(this.getFromDateValue()))} - ${formatMasterDataMonthLabel(this.normalizeMonthValue(this.getToDateValue()))}`;
    }

    return `${this.formatDisplayDate(this.getFromDateValue())} - ${this.formatDisplayDate(this.getToDateValue())}`;
  }

  protected getSelectedExchangeLabel(): string {
    return this.selectedExchanges.length === this.exchangeOptions.length ? 'All Exchanges' : this.selectedExchanges.join(', ');
  }

  protected getSelectedProductLabel(): string {
    return this.selectedProducts.length === this.productOptions.length ? 'All Products' : this.selectedProducts.join(', ');
  }

  protected isExchangeSelected(value: MasterDataExchangeCode): boolean {
    return this.selectedExchanges.includes(value);
  }

  protected isProductSelected(value: MasterDataProductCode): boolean {
    return this.selectedProducts.includes(value);
  }

  protected toggleExchangeSelection(option: MasterDataExchangeCode, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (target?.checked) {
      if (!this.selectedExchanges.includes(option)) {
        this.selectedExchanges = [...this.selectedExchanges, option];
      }
    } else if (this.selectedExchanges.length > 1) {
      this.selectedExchanges = this.selectedExchanges.filter((value) => value !== option);
    } else if (target) {
      target.checked = true;
    }

    this.loadReport();
  }

  protected toggleProductSelection(option: MasterDataProductCode, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (target?.checked) {
      if (!this.selectedProducts.includes(option)) {
        this.selectedProducts = [...this.selectedProducts, option];
      }
    } else if (this.selectedProducts.length > 1) {
      this.selectedProducts = this.selectedProducts.filter((value) => value !== option);
    } else if (target) {
      target.checked = true;
    }

    this.loadReport();
  }

  protected formatNumber(value: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
      return '-';
    }
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  protected trackByDateGroup(_: number, group: MasterDataHeaderGroup): string {
    return group.key;
  }

  protected trackByExchangeGroup(_: number, group: MasterDataHeaderGroup): string {
    return group.key;
  }

  protected trackByProductGroup(_: number, group: MasterDataHeaderGroup): string {
    return group.key;
  }

  protected trackByLeafColumn(_: number, column: MasterDataLeafColumn): string {
    return column.key;
  }

  protected trackByBlock(_: number, block: number): number {
    return block;
  }

  protected trackByRowLabel(_: number, row: MasterDataRowLabel): string {
    return row.key;
  }

  protected getBlockTime(blockNo: number): string {
    return this.blockTimes[blockNo - 1] ?? '-';
  }

  protected getEmptyStateColspan(): number {
    return this.isBlockMode() ? Math.max(2, 2 + this.leafColumns.length) : Math.max(2, 2 + this.periodLeafColumns.length);
  }

  protected getCellText(blockNo: number, column: MasterDataLeafColumn): string {
    const dateBucket = this.blockCellMap[column.dateValue ?? ''];
    const exchangeBucket = dateBucket?.[column.exchangeValue];
    const productBucket = exchangeBucket?.[column.productValue];
    const cell = productBucket?.[String(blockNo)] ?? null;
    const value = column.metric === 'MCP' ? cell?.mcp ?? null : cell?.mcv ?? null;
    return this.formatNumber(value);
  }

  protected getColumnMetricClass(column: MasterDataLeafColumn): string {
    const metricClass = `master-data-table__metric--${column.metric.toLowerCase()}`;
    return column.metric === 'MCV' ? `${metricClass} master-data-table__metric--group-end` : metricClass;
  }

  protected getPeriodCellText(rowKey: string, column: MasterDataLeafColumn): string {
    const periodBucket = this.periodCellMap[rowKey];
    const exchangeBucket = periodBucket?.[column.exchangeValue];
    const productBucket = exchangeBucket?.[column.productValue];
    const cell = productBucket ?? null;
    const value = column.metric === 'MCP' ? cell?.mcp ?? null : cell?.mcv ?? null;
    return this.formatNumber(value);
  }

  protected getBlockLabel(blockNo: number): string {
    return String(blockNo);
  }

  protected getRowLabel(rowKey: string): string {
    return this.rowLabels.find((item) => item.key === rowKey)?.label ?? rowKey;
  }

  protected getFrequencyLabel(): string {
    return this.frequencyOptions.find((option) => option.value === this.selectedFrequency)?.label ?? '15 Min';
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected getDateInputType(): 'date' | 'month' | 'text' {
    if (this.selectedFrequency === 'MONTHLY') {
      return 'month';
    }
    return this.isBsDateMode ? 'text' : 'date';
  }

  protected openBsDatePicker(controlName: 'from_date' | 'to_date'): void {
    if (!this.isBsDateMode || this.isMonthlyFrequency()) {
      return;
    }
    if (this.bsCalendarOpen && this.activeBsDateControl === controlName) {
      this.closeBsDatePicker();
      return;
    }
    this.activeBsDateControl = controlName;
    void this.loadBsCalendar(this.getDateControlValue(controlName));
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected selectBsCalendarCell(cell: MarketDataBsCalendarCell): void {
    if (!cell.bs_date) {
      return;
    }
    this.closeBsDatePicker();
    this.filtersForm.patchValue({ [this.activeBsDateControl]: formatBsDate(cell.bs_date) });
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !offset) {
      return;
    }
    let month = this.bsCalendarMonth.bs_month + offset;
    let year = this.bsCalendarMonth.bs_year;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    if (month > 12) {
      month = 1;
      year += 1;
    }
    void this.loadBsCalendar(`${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-01`);
  }

  protected isMonthlyFrequency(): boolean {
    return this.selectedFrequency === 'MONTHLY';
  }

  protected onFromMonthSelected(selectedMonth: Date, picker: MatDatepicker<Date>): void {
    const normalizedMonth = this.normalizeMonthDate(selectedMonth);
    this.fromMonthPickerDate = normalizedMonth;
    this.filtersForm.patchValue(
      {
        from_date: this.formatMonthControlValue(normalizedMonth),
      },
      { emitEvent: true },
    );
    picker.close();
  }

  protected onToMonthSelected(selectedMonth: Date, picker: MatDatepicker<Date>): void {
    const normalizedMonth = this.normalizeMonthDate(selectedMonth);
    this.toMonthPickerDate = normalizedMonth;
    this.filtersForm.patchValue(
      {
        to_date: this.formatMonthControlValue(normalizedMonth),
      },
      { emitEvent: true },
    );
    picker.close();
  }

  protected isBlockMode(): boolean {
    return this.viewMode === 'BLOCK';
  }

  protected isPeriodMode(): boolean {
    return this.viewMode === 'PERIOD';
  }

  protected getMetricUnit(metric: MasterDataMetricCode): string {
    return this.metricUnits[metric];
  }

  protected getRowAxisTitle(): string {
    return this.rowAxisTitle;
  }

  protected getMaxToDate(): string {
    if (this.selectedFrequency === 'MONTHLY') {
      return this.normalizeMonthValue(this.getToDateValue());
    }
    return this.shiftDate(this.getFromDateValue(), MASTER_DATA_MAX_RANGE_DAYS);
  }

  protected getMinFromDate(): string {
    if (this.selectedFrequency === 'MONTHLY') {
      return this.normalizeMonthValue(this.getFromDateValue());
    }
    return this.shiftDate(this.getToDateValue(), -MASTER_DATA_MAX_RANGE_DAYS);
  }

  protected isRangeTooLarge(): boolean {
    const fromDate = this.getNormalizedFromDateValue();
    const toDate = this.getNormalizedToDateValue();
    if (!fromDate || !toDate) {
      return false;
    }

    if (this.selectedFrequency === 'MONTHLY') {
      return this.dateDiffInMonths(this.normalizeMonthValue(fromDate), this.normalizeMonthValue(toDate)) > MASTER_DATA_MAX_RANGE_MONTHS;
    }

    return this.dateDiffInDays(fromDate, toDate) > MASTER_DATA_MAX_RANGE_DAYS;
  }

  private bindFilterListeners(): void {
    this.filtersForm
      .get('from_date')
      ?.valueChanges.pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadReport());
    this.filtersForm
      .get('to_date')
      ?.valueChanges.pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadReport());
    this.filtersForm
      .get('data_frequency')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleFrequencySelection(value));
  }

  private handleFrequencySelection(value: unknown): void {
    const nextFrequency = this.normalizeFrequencyValue(value);
    if (nextFrequency === this.selectedFrequency) {
      return;
    }

    this.selectedFrequency = nextFrequency;
    this.applyFrequencyDefaults(nextFrequency, false);
    this.syncMonthPickerState();
    if (this.isBsDateMode) {
      void this.convertDateControls('BS').then(() => this.loadReport());
      return;
    }
    this.loadReport();
  }

  private applyFrequencyDefaults(frequency: MasterDataFrequencyCode, emitEvent: boolean): void {
    const fromDateValue = this.getDefaultFromDateForFrequency(frequency);
    const toDateValue = this.getDefaultToDateForFrequency(frequency);
    this.filtersForm.patchValue(
      {
        from_date: fromDateValue,
        to_date: toDateValue,
        data_frequency: frequency,
      },
      { emitEvent },
    );
    this.syncMonthPickerState();
  }

  private getDefaultFromDateForFrequency(frequency: MasterDataFrequencyCode): string {
    if (frequency === 'MONTHLY') {
      return getCurrentMonthValue();
    }

    if (frequency === 'DAILY') {
      return getDefaultDailyFromDateValue();
    }

    return getTodayDateValue();
  }

  private getDefaultToDateForFrequency(frequency: MasterDataFrequencyCode): string {
    return frequency === 'MONTHLY' ? getCurrentMonthValue() : getTodayDateValue();
  }

  private loadReport(): void {
    if (this.isRangeTooLarge()) {
      this.errorMessage = this.selectedFrequency === 'MONTHLY'
        ? `Select a month range of ${MASTER_DATA_MAX_RANGE_MONTHS} months or less.`
        : `Select a date range of ${MASTER_DATA_MAX_RANGE_DAYS} days or less.`;
      this.resetReportState();
      return;
    }

    this.errorMessage = '';
    this.isLoading = true;
    this.httpSvc
      .requestCall(AuthEndPoints.GET_MASTER_DATA_REPORT, ApiMethod.POST, this.buildReportPayload(), false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => this.applyReport(this.extractReport(response)),
        error: () => this.applyReport(null),
      });
  }

  private applyReport(report: MasterDataReportResponse | null): void {
    if (!report) {
      this.resetReportState();
      this.syncMonthPickerState();
      return;
    }

    this.dateLabels = report.date_labels ?? {};
    const frequency = this.normalizeFrequencyValue(report.frequency);
    this.selectedFrequency = frequency;
    this.syncMonthPickerState();
    this.viewMode = frequency === '15_MIN' ? 'BLOCK' : 'PERIOD';
    this.rowAxisTitle = report.row_axis_title ?? this.resolveRowAxisTitle(frequency);
    this.metricUnits = {
      MCP: report.metric_units?.MCP ?? '₹/MWh',
      MCV: report.metric_units?.MCV ?? (frequency === '15_MIN' ? 'MW' : 'MWh'),
    };

    const dates = report.dates.length > 0 ? report.dates : this.buildDateRange(this.getNormalizedFromDateValue(), this.getNormalizedToDateValue());
    const exchanges = report.exchanges.length > 0 ? report.exchanges : [...MASTER_DATA_EXCHANGES];
    const products = report.products.length > 0 ? report.products : [...MASTER_DATA_PRODUCTS];

    if (this.isBlockMode()) {
      this.blockRows = report.block_numbers.length > 0 ? report.block_numbers : [...MASTER_DATA_DEFAULT_BLOCKS];
      this.blockTimes = report.block_times.length > 0 ? report.block_times : this.blockRows.map((block) => String(block));
      this.dateGroups = this.buildDateGroups(dates, exchanges, products);
      this.exchangeGroups = this.buildBlockExchangeGroups(dates, exchanges, products);
      this.productGroups = this.buildBlockProductGroups(dates, exchanges, products);
      this.leafColumns = this.buildBlockLeafColumns(dates, exchanges, products);
      this.blockCellMap = (report.grid as MasterDataGridDateMap) ?? {};
      this.periodExchangeGroups = [];
      this.periodProductGroups = [];
      this.periodLeafColumns = [];
      this.periodCellMap = {};
      this.rowLabels = report.row_labels ?? this.blockRows.map((block, index) => ({ key: String(block), label: this.blockTimes[index] ?? String(block) }));
    } else {
      this.blockRows = [];
      this.blockTimes = [];
      this.dateGroups = [];
      this.exchangeGroups = this.buildPeriodExchangeGroups(exchanges, products);
      this.productGroups = this.buildPeriodProductGroups(exchanges, products);
      this.leafColumns = this.buildPeriodLeafColumns(exchanges, products);
      this.blockCellMap = {};
      this.rowLabels = report.row_labels ?? [];
      this.periodExchangeGroups = this.exchangeGroups;
      this.periodProductGroups = this.productGroups;
      this.periodLeafColumns = this.leafColumns;
      this.periodCellMap = (report.grid as MasterDataGridPeriodMap) ?? {};
    }

    this.summary = report.summary;
  }

  private resetReportState(): void {
    this.blockRows = [];
    this.blockTimes = [];
    this.dateGroups = [];
    this.dateLabels = {};
    this.exchangeGroups = [];
    this.productGroups = [];
    this.leafColumns = [];
    this.periodExchangeGroups = [];
    this.periodProductGroups = [];
    this.periodLeafColumns = [];
    this.blockCellMap = {};
    this.periodCellMap = {};
    this.rowLabels = [];
    this.rowAxisTitle = 'Time';
    this.viewMode = 'BLOCK';
    this.summary = {
      date_count: 0,
      row_count: 0,
      exchange_count: 0,
      product_count: 0,
      block_count: 0,
      cell_count: 0,
      frequency: MASTER_DATA_DEFAULT_FREQUENCY,
    };
    this.metricUnits = {
      MCP: '₹/MWh',
      MCV: 'MW',
    };
  }

  private buildDateGroups(dates: string[], exchanges: MasterDataExchangeCode[], products: MasterDataProductCode[]): MasterDataHeaderGroup[] {
    const span = exchanges.length * products.length * MASTER_DATA_METRICS.length;
    return dates.map((dateValue) => ({
      key: dateValue,
      label: this.dateLabels[dateValue] ?? formatMasterDataDateLabel(dateValue),
      span,
    }));
  }

  private buildExchangeGroups(
    dates: string[],
    exchanges: MasterDataExchangeCode[],
    products: MasterDataProductCode[],
  ): MasterDataHeaderGroup[] {
    const span = products.length * MASTER_DATA_METRICS.length;
    const groups: MasterDataHeaderGroup[] = [];
    for (const dateValue of dates) {
      for (const exchange of exchanges) {
        groups.push({
          key: `${dateValue}-${exchange}`,
          label: exchange,
          span,
        });
      }
    }
    return groups;
  }

  private buildBlockExchangeGroups(
    dates: string[],
    exchanges: MasterDataExchangeCode[],
    products: MasterDataProductCode[],
  ): MasterDataHeaderGroup[] {
    return this.buildExchangeGroups(dates, exchanges, products);
  }

  private buildProductGroups(
    dates: string[],
    exchanges: MasterDataExchangeCode[],
    products: MasterDataProductCode[],
  ): MasterDataHeaderGroup[] {
    const groups: MasterDataHeaderGroup[] = [];
    for (const dateValue of dates) {
      for (const exchange of exchanges) {
        for (const product of products) {
          groups.push({
            key: `${dateValue}-${exchange}-${product}`,
            label: product,
            span: MASTER_DATA_METRICS.length,
          });
        }
      }
    }
    return groups;
  }

  private buildBlockProductGroups(
    dates: string[],
    exchanges: MasterDataExchangeCode[],
    products: MasterDataProductCode[],
  ): MasterDataHeaderGroup[] {
    return this.buildProductGroups(dates, exchanges, products);
  }

  private buildLeafColumns(
    dates: string[],
    exchanges: MasterDataExchangeCode[],
    products: MasterDataProductCode[],
  ): MasterDataLeafColumn[] {
    const columns: MasterDataLeafColumn[] = [];
    for (const dateValue of dates) {
      for (const exchange of exchanges) {
        for (const product of products) {
          for (const metric of MASTER_DATA_METRICS) {
            columns.push({
              key: `${dateValue}-${exchange}-${product}-${metric}`,
              dateValue,
              exchangeValue: exchange,
              productValue: product,
              metric,
              label: metric === 'MCP' ? 'MCP (₹/MWh)' : 'MCV (MW)',
            });
          }
        }
      }
    }
    return columns;
  }

  private buildBlockLeafColumns(
    dates: string[],
    exchanges: MasterDataExchangeCode[],
    products: MasterDataProductCode[],
  ): MasterDataLeafColumn[] {
    return this.buildLeafColumns(dates, exchanges, products);
  }

  private buildPeriodExchangeGroups(exchanges: MasterDataExchangeCode[], products: MasterDataProductCode[]): MasterDataHeaderGroup[] {
    const span = products.length * MASTER_DATA_METRICS.length;
    return exchanges.map((exchange) => ({
      key: exchange,
      label: exchange,
      span,
    }));
  }

  private buildPeriodProductGroups(exchanges: MasterDataExchangeCode[], products: MasterDataProductCode[]): MasterDataHeaderGroup[] {
    const groups: MasterDataHeaderGroup[] = [];
    for (const exchange of exchanges) {
      for (const product of products) {
        groups.push({
          key: `${exchange}-${product}`,
          label: product,
          span: MASTER_DATA_METRICS.length,
        });
      }
    }
    return groups;
  }

  private buildPeriodLeafColumns(exchanges: MasterDataExchangeCode[], products: MasterDataProductCode[]): MasterDataLeafColumn[] {
    const columns: MasterDataLeafColumn[] = [];
    for (const exchange of exchanges) {
      for (const product of products) {
        for (const metric of MASTER_DATA_METRICS) {
          columns.push({
            key: `${exchange}-${product}-${metric}`,
            exchangeValue: exchange,
            productValue: product,
            metric,
            label: metric === 'MCP' ? 'MCP (₹/MWh)' : 'MCV (MWh)',
          });
        }
      }
    }
    return columns;
  }

  private buildReportPayload(): Record<string, unknown> {
    const fromDate =
      this.selectedFrequency === 'MONTHLY'
        ? getMonthStartDateValue(this.normalizeMonthValue(this.getFromDateValue()))
        : this.getFromDateValue();
    const toDate =
      this.selectedFrequency === 'MONTHLY'
        ? getMonthEndDateValue(this.normalizeMonthValue(this.getToDateValue()))
        : this.getToDateValue();
    return {
      from_date: this.toApiDateValue(fromDate),
      to_date: this.toApiDateValue(toDate),
      exchanges: this.selectedExchanges,
      products: this.selectedProducts,
      data_frequency: this.selectedFrequency,
      bs_ad_key: this.isBsDateMode ? 'BS' : 'AD',
    };
  }

  private toApiDateValue(value: string): string {
    return this.isBsDateMode && value.includes('/') ? parseBsDate(value) : value;
  }

  private listenToDatePreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }
    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name === 'showBsDate') {
          this.closeBsDatePicker();
          void this.handleDateSystemChange();
        }
      });
  }

  private async handleDateSystemChange(): Promise<void> {
    await this.convertDateControls(this.isBsDateMode ? 'BS' : 'AD');
    this.loadReport();
  }

  private async loadBsCalendar(dateValue: string): Promise<void> {
    if (!dateValue) {
      return;
    }
    this.bsCalendarOpen = true;
    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
        ApiMethod.POST,
        { data_date: parseBsDate(dateValue), bs_ad_key: 'BS' },
        false,
        { suppressToast: true },
      ));
      const value = response?.value ?? {};
      this.bsCalendarMonth = {
        bs_year: Number(value.bs_year) || 0,
        bs_month: Number(value.bs_month) || 0,
        bs_month_name: value.bs_month_name || '',
        cells: Array.isArray(value.cells) ? value.cells : [],
      };
    } catch {
      this.bsCalendarMonth = null;
    } finally {
      this.bsCalendarLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async convertDateControls(targetMode: 'AD' | 'BS'): Promise<void> {
    const sourceMode = targetMode === 'BS' ? 'AD' : 'BS';
    const controls = ['from_date', 'to_date'] as const;
    const convertedValues = await Promise.all(controls.map(async (controlName) => {
      const value = this.getDateControlValue(controlName);
      if (!value) {
        return null;
      }
      try {
        const response = await firstValueFrom(this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          {
            data_date: sourceMode === 'BS' ? parseBsDate(value) : value,
            bs_ad_key: targetMode,
            source_bs_ad_key: sourceMode,
          },
          false,
          { suppressToast: true },
        ));
        const result = response?.value;
        const nextValue = targetMode === 'BS' ? result?.bs_date_label : result?.data_date;
        return typeof nextValue === 'string' ? [controlName, nextValue] as const : null;
      } catch {
        return null;
      }
    }));
    const patch = Object.fromEntries(convertedValues.filter((entry): entry is readonly ['from_date' | 'to_date', string] => entry !== null));
    if (Object.keys(patch).length) {
      this.filtersForm.patchValue(patch, { emitEvent: false });
    }
  }

  private getDateControlValue(controlName: 'from_date' | 'to_date'): string {
    const value = this.filtersForm.get(controlName)?.value;
    return typeof value === 'string' ? value : '';
  }

  private buildExportFileName(): string {
    const fromDate = this.selectedFrequency === 'MONTHLY' ? this.normalizeMonthValue(this.getFromDateValue()) : this.getFromDateValue();
    const toDate = this.selectedFrequency === 'MONTHLY' ? this.normalizeMonthValue(this.getToDateValue()) : this.getToDateValue();
    return `market_data_report_${fromDate}_${toDate}.xlsx`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }

  private extractReport(response: unknown): MasterDataReportResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    return {
      from_date: this.extractStringValue(value, 'from_date') ?? this.getFromDateValue(),
      to_date: this.extractStringValue(value, 'to_date') ?? this.getToDateValue(),
      frequency: this.extractFrequency(value),
      row_axis_title: this.extractStringValue(value, 'row_axis_title') ?? undefined,
      dates: this.getStringArrayValue(value, 'dates'),
      date_labels: this.extractDateLabels(value),
      row_labels: this.getRowLabels(value),
      exchanges: this.getTypedExchangeArray(value, 'exchanges'),
      products: this.getTypedProductArray(value, 'products'),
      block_numbers: this.getNumberArrayValue(value, 'block_numbers'),
      block_times: this.getStringArrayValue(value, 'block_times'),
      grid: this.getGridValue(value, 'grid'),
      summary: this.extractSummary(value),
      metric_units: this.extractMetricUnits(value),
    };
  }

  private extractDateLabels(value: Record<string, unknown>): Record<string, string> {
    const labels = this.getRecordValue(value, 'date_labels');
    if (!labels || typeof labels !== 'object') {
      return {};
    }
    return Object.entries(labels).reduce<Record<string, string>>((result, [dateValue, label]) => {
      if (typeof label === 'string' && label.trim()) {
        result[dateValue] = label;
      }
      return result;
    }, {});
  }

  private extractSummary(value: unknown): MasterDataSummary {
    const summaryRecord = this.getRecordValue(value, 'summary');
    const record = this.getRecord(summaryRecord);
    return {
      date_count: this.normalizeNumber(record['date_count']) ?? 0,
      row_count: this.normalizeNumber(record['row_count']) ?? this.normalizeNumber(record['date_count']) ?? 0,
      exchange_count: this.normalizeNumber(record['exchange_count']) ?? 0,
      product_count: this.normalizeNumber(record['product_count']) ?? 0,
      block_count: this.normalizeNumber(record['block_count']) ?? 0,
      cell_count: this.normalizeNumber(record['cell_count']) ?? 0,
      frequency: this.normalizeFrequencyValue(record['frequency'] ?? null),
    };
  }

  private getGridValue(value: unknown, key: string): MasterDataGridDateMap | MasterDataGridPeriodMap {
    const candidate = this.getRecordValue(value, key);
    return candidate && typeof candidate === 'object' ? (candidate as MasterDataGridDateMap | MasterDataGridPeriodMap) : {};
  }

  private getTypedExchangeArray(value: unknown, key: string): MasterDataExchangeCode[] {
    const allowed = new Set<string>(MASTER_DATA_EXCHANGES);
    return this.getStringArrayValue(value, key).filter((item): item is MasterDataExchangeCode => allowed.has(item));
  }

  private getTypedProductArray(value: unknown, key: string): MasterDataProductCode[] {
    const allowed = new Set<string>(MASTER_DATA_PRODUCTS);
    return this.getStringArrayValue(value, key).filter((item): item is MasterDataProductCode => allowed.has(item));
  }

  protected getFromDateValue(): string {
    const value = this.filtersForm.get('from_date')?.value;
    return typeof value === 'string' && value.length > 0 ? value : getTodayDateValue();
  }

  protected getToDateValue(): string {
    const value = this.filtersForm.get('to_date')?.value;
    return typeof value === 'string' && value.length > 0 ? value : getTodayDateValue();
  }

  private getNormalizedFromDateValue(): string {
    if (this.selectedFrequency === 'MONTHLY') {
      return getMonthStartDateValue(this.normalizeMonthValue(this.getFromDateValue()));
    }
    return this.toApiDateValue(this.getFromDateValue());
  }

  private getNormalizedToDateValue(): string {
    if (this.selectedFrequency === 'MONTHLY') {
      return getMonthEndDateValue(this.normalizeMonthValue(this.getToDateValue()));
    }
    return this.toApiDateValue(this.getToDateValue());
  }

  private normalizeMonthValue(value: string): string {
    if (value.length >= 7) {
      return value.slice(0, 7);
    }
    return getCurrentMonthValue();
  }

  private formatMonthControlValue(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  private parseMonthControlValue(value: string): Date | null {
    const normalizedValue = this.normalizeMonthValue(value);
    const parsedDate = new Date(`${normalizedValue}-01T00:00:00`);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private normalizeMonthDate(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), 1);
  }

  private syncMonthPickerState(): void {
    if (!this.isMonthlyFrequency()) {
      this.fromMonthPickerDate = null;
      this.toMonthPickerDate = null;
      return;
    }

    this.fromMonthPickerDate = this.parseMonthControlValue(this.getFromDateValue());
    this.toMonthPickerDate = this.parseMonthControlValue(this.getToDateValue());
  }

  private normalizeFrequencyValue(value: unknown): MasterDataFrequencyCode {
    if (typeof value !== 'string') {
      return MASTER_DATA_DEFAULT_FREQUENCY;
    }
    const candidate = value.trim().toUpperCase().replace(/\s+/g, '_');
    if (candidate === 'DAILY' || candidate === 'MONTHLY' || candidate === '15_MIN' || candidate === '15MIN') {
      return candidate === '15MIN' ? '15_MIN' : (candidate as MasterDataFrequencyCode);
    }
    return MASTER_DATA_DEFAULT_FREQUENCY;
  }

  private resolveRowAxisTitle(frequency: MasterDataFrequencyCode): string {
    if (frequency === 'MONTHLY') {
      return 'Month';
    }
    if (frequency === 'DAILY') {
      return 'Date';
    }
    return 'Time';
  }

  private dateDiffInMonths(fromValue: string, toValue: string): number {
    const normalizedFrom = fromValue.length >= 7 ? `${fromValue.slice(0, 7)}-01` : fromValue;
    const normalizedTo = toValue.length >= 7 ? `${toValue.slice(0, 7)}-01` : toValue;
    const start = new Date(`${normalizedFrom}T00:00:00`);
    const end = new Date(`${normalizedTo}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 0;
    }

    return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
  }

  private extractFrequency(value: unknown): MasterDataFrequencyCode {
    return this.normalizeFrequencyValue(this.getRecordValue(value, 'frequency'));
  }

  private getRowLabels(value: unknown): MasterDataRowLabel[] {
    return this.getArrayValue(value, 'row_labels')
      .map((item) => this.getRecord(item))
      .map((record) => {
        const key = typeof record['key'] === 'string' ? record['key'].trim() : '';
        const label = typeof record['label'] === 'string' ? record['label'].trim() : '';
        return key.length > 0 && label.length > 0 ? { key, label } : null;
      })
      .filter((item): item is MasterDataRowLabel => item !== null);
  }

  private extractMetricUnits(value: unknown): { MCP: string; MCV: string } | undefined {
    const record = this.getRecord(this.getRecordValue(value, 'metric_units'));
    const mcp = typeof record['MCP'] === 'string' && record['MCP'].trim().length > 0 ? record['MCP'].trim() : '₹/MWh';
    const mcv = typeof record['MCV'] === 'string' && record['MCV'].trim().length > 0 ? record['MCV'].trim() : this.metricUnits.MCV;
    return { MCP: mcp, MCV: mcv };
  }

  private buildDateRange(fromValue: string, toValue: string): string[] {
    const dates: string[] = [];
    const start = new Date(`${fromValue}T00:00:00`);
    const end = new Date(`${toValue}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return dates;
    }

    const cursor = new Date(start);
    while (cursor <= end) {
      dates.push(cursor.toISOString().slice(0, 10));
      cursor.setDate(cursor.getDate() + 1);
    }

    return dates;
  }

  private dateDiffInDays(fromValue: string, toValue: string): number {
    const start = new Date(`${fromValue}T00:00:00`);
    const end = new Date(`${toValue}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 0;
    }
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  }

  private shiftDate(dateValue: string, offsetDays: number): string {
    const parsed = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return dateValue;
    }

    parsed.setDate(parsed.getDate() + offsetDays);
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDisplayDate(value: string): string {
    return this.isBsDateMode && value.includes('/') ? value : formatMasterDataDateLabel(value);
  }

  private extractResponseValue(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const root = this.getRecord(response);
    const value = root['value'];
    return value && typeof value === 'object' ? this.getRecord(value) : null;
  }

  private getArrayValue(value: unknown, key: string): unknown[] {
    const candidate = this.getRecordValue(value, key);
    return Array.isArray(candidate) ? candidate : [];
  }

  private getStringArrayValue(value: unknown, key: string): string[] {
    return this.getArrayValue(value, key)
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0);
  }

  private getNumberArrayValue(value: unknown, key: string): number[] {
    return this.getArrayValue(value, key)
      .map((item) => this.normalizeNumber(item))
      .filter((item): item is number => item !== null);
  }

  private getRecordValue(value: unknown, key: string): unknown {
    const record = this.getRecord(value);
    return record[key] ?? null;
  }

  private getRecord(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object') {
      return {};
    }
    return { ...(value as Record<string, unknown>) };
  }

  private extractStringValue(value: unknown, key: string): string | null {
    const candidate = this.getRecordValue(value, key);
    if (typeof candidate !== 'string') {
      return null;
    }
    const trimmed = candidate.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }
    return numericValue;
  }
}
